import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { formatOfficerDateTime } from '../lib/officerFormat'
import { ErrorState } from './state'
import { rememberFundChoice } from './fundScope'
import {
  fetchDocuments,
  fetchOrphans,
  documentNeedsAttention,
  documentReceivedSince,
  PROCESSING,
  type DocRow,
} from './documentsList'

/** Document counts share Today's session boundary and refresh clock. Processing
 * and attention are current states, never stages of an invented booking funnel. */
export default function DocumentActivity({
  since,
  fundId,
  refreshKey,
}: {
  since: string
  fundId: string | null
  refreshKey: number
}) {
  const [data, setData] = useState<{ rows: DocRow[]; parked: Set<string> } | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [retry, setRetry] = useState(0)
  const lastRetry = useRef<number | null>(null)
  const active = useRef<AbortController | null>(null)
  useEffect(() => () => active.current?.abort(), [])
  useEffect(() => {
    // A slow paginated read must be allowed to finish across heartbeats.
    // An explicit Retry replaces it; leaving the page aborts it.
    if (active.current && !active.current.signal.aborted && lastRetry.current === retry) return
    active.current?.abort()
    const controller = new AbortController()
    active.current = controller
    const background = lastRetry.current === retry
    lastRetry.current = retry
    Promise.all([
      fetchDocuments({ signal: controller.signal, background }),
      fetchOrphans({ signal: controller.signal, background }),
    ])
      .then(([rows, parked]) => {
        if (!controller.signal.aborted) {
          setData({ rows, parked: new Set(parked.map((d) => d.document_id)) })
          setError(null)
        }
      })
      .catch((e) => {
        if (!controller.signal.aborted) setError(e)
      })
      .finally(() => {
        if (active.current === controller) active.current = null
      })
  }, [refreshKey, retry])

  const scoped = data?.rows.filter((d) => !fundId || d.fund_ids?.includes(fundId)) ?? []
  const href = (status?: string, received = false, allFunds = false) => {
    const p = new URLSearchParams({ fund: allFunds ? '' : (fundId ?? '') })
    if (status) p.set('status', status)
    if (received) p.set('received_since', since)
    return `/v2/traces?${p}`
  }
  const unmatched = data?.rows.filter((d) => data.parked.has(d.id)).length ?? 0
  return (
    <section
      className="today-documents"
      aria-label="Document activity"
      data-testid="today-document-activity"
    >
      <div className="today-documents-title">
        Documents <span>Received since {formatOfficerDateTime(since)} · status counts now</span>
      </div>
      {error ? (
        <ErrorState
          what="document activity"
          error={error}
          onRetry={() => setRetry((v) => v + 1)}
          testId="today-documents-error"
        />
      ) : !data ? (
        <p role="status">Loading document activity…</p>
      ) : (
        <>
          <div className="today-document-counts">
            <Link to={href(undefined, true)} data-testid="today-documents-received">
              <b>{scoped.filter((d) => documentReceivedSince(d, since)).length}</b> received since
              this window began <span>›</span>
            </Link>
            <Link to={href('processing')} data-testid="today-documents-processing">
              <b>{scoped.filter((d) => PROCESSING.has(d.status)).length}</b> processing now{' '}
              <span>›</span>
            </Link>
            <Link to={href('attention')} data-testid="today-documents-attention">
              <b>{scoped.filter((d) => documentNeedsAttention(d, data.parked)).length}</b> need
              attention now <span>›</span>
            </Link>
          </div>
          {fundId && unmatched > 0 && (
            <Link
              className="today-document-unmatched"
              to={href('unmatched', false, true)}
              onClick={() => rememberFundChoice(null)}
            >
              {unmatched} unmatched {unmatched === 1 ? 'document' : 'documents'} across all funds ›
            </Link>
          )}
        </>
      )}
    </section>
  )
}
